// Vue 2 wrapper for Floating UI
// Based on official Vue 3 implementation: https://github.com/floating-ui/floating-ui/blob/master/packages/vue/src/useFloating.ts
import Vue from 'vue'
import { computePosition, arrow as arrowMiddleware } from '@floating-ui/dom'

/**
 * Returns the device pixel ratio for the current window or element
 * @param {Element} element - The element to check
 * @returns {number} The device pixel ratio
 */
const getDPR = (element) => {
    if (typeof window === 'undefined') {
        return 1
    }
    const win = element.ownerDocument.defaultView || window
    return win.devicePixelRatio || 1
}

/**
 * Rounds a value based on the device pixel ratio
 * @param {Element} element - The element to use for DPR context
 * @param {number} value - The value to round
 * @returns {number} The rounded value
 */
const roundByDPR = (element, value) => {
    const dpr = getDPR(element)
    return Math.round(value * dpr) / dpr
}

/**
 * Unwraps a Vue component to its underlying DOM element
 * @param {Object|Element} element - The element or Vue component to unwrap
 * @returns {Element} The underlying DOM element
 */
const unwrapElement = (element) => {
    // unwrap Vue components
    if (element && element.$el) {
        return element.$el
    }
    return element
}

/**
 * Extracts a value from a function, Vue ref, or raw value
 * @param {*} value - The value to extract from
 * @returns {*} The extracted value
 */
const toValue = (value) => {
    if (typeof value === 'function') {
        return value()
    }
    // Handle Vue refs
    if (value && typeof value === 'object' && 'value' in value) {
        return value.value
    }
    return value
}

/**
 * Vue wrapper for floating-ui arrow middleware.
 * Handles Vue refs and components.
 *
 * @param {Object} options - Arrow options
 * @param options.element - Element ref, getter func, or DOM element
 * @param options.padding - Padding from tooltip edges
 * @returns {Object} The floating-ui middleware object
 * @see https://floating-ui.com/docs/arrow
 */
const arrow = (options) => ({
    name: 'arrow',
    options,
    fn(args) {
        // find element on every position calculation, since it might not exist immediately
        const element = unwrapElement(toValue(options.element))
        if (element == null) {
            return {}
        }
        return arrowMiddleware({ element, padding: options.padding }).fn(args)
    },
})

/**
 * Computes the `x` and `y` coordinates that will place the floating element
 * next to a reference element when it is given a certain CSS positioning strategy.
 *
 * @param {Object} reference - Object with a 'value' property containing the reference element
 * @param {Object} floating - Object with a 'value' property containing the floating element
 * @param {Object} options - Floating UI options
 * @returns {Object} Reactive floating state and methods
 * @see https://floating-ui.com/docs/vue
 */
const useFloating = (reference, floating, options = {}) => {
    const vm = new Vue({
        data() {
            return {
                x: 0,
                y: 0,
                strategy: toValue(options.strategy) || 'absolute',
                placement: toValue(options.placement) || 'bottom',
                middlewareData: {},
                isPositioned: false,
                whileElementsMountedCleanup: null,
            }
        },
        computed: {
            openOption() {
                return toValue(options.open) ?? true
            },
            middlewareOption() {
                return toValue(options.middleware)
            },
            placementOption() {
                return toValue(options.placement) || 'bottom'
            },
            strategyOption() {
                return toValue(options.strategy) || 'absolute'
            },
            transformOption() {
                return toValue(options.transform) ?? true
            },
            referenceElement() {
                return unwrapElement(reference.value)
            },
            floatingElement() {
                return unwrapElement(floating.value)
            },
            floatingStyles() {
                const initialStyles = {
                    position: this.strategy,
                    left: '0',
                    top: '0',
                }

                if (!this.floatingElement) {
                    return initialStyles
                }

                const x = roundByDPR(this.floatingElement, this.x)
                const y = roundByDPR(this.floatingElement, this.y)

                if (this.transformOption) {
                    return {
                        ...initialStyles,
                        transform: `translate(${x}px, ${y}px)`,
                        ...(getDPR(this.floatingElement) >= 1.5 && {
                            willChange: 'transform',
                        }),
                    }
                }

                return {
                    position: this.strategy,
                    left: `${x}px`,
                    top: `${y}px`,
                }
            },
        },
        watch: {
            middlewareOption: {
                handler: 'update',
                immediate: false,
            },
            placementOption: {
                handler: 'update',
                immediate: false,
            },
            strategyOption: {
                handler: 'update',
                immediate: false,
            },
            openOption: {
                handler: 'reset',
                immediate: false,
            },
            referenceElement: {
                handler: 'attach',
                immediate: false,
            },
            floatingElement: {
                handler: 'attach',
                immediate: false,
            },
        },
        methods: {
            update() {
                if (
                    this.referenceElement == null ||
                    this.floatingElement == null
                ) {
                    return Promise.resolve()
                }

                const open = this.openOption

                return computePosition(
                    this.referenceElement,
                    this.floatingElement,
                    {
                        middleware: this.middlewareOption,
                        placement: this.placementOption,
                        strategy: this.strategyOption,
                    }
                ).then((position) => {
                    this.x = position.x
                    this.y = position.y
                    this.strategy = position.strategy
                    this.placement = position.placement
                    this.middlewareData = position.middlewareData
                    this.isPositioned = open !== false

                    return position
                })
            },
            cleanup() {
                if (typeof this.whileElementsMountedCleanup === 'function') {
                    this.whileElementsMountedCleanup()
                    this.whileElementsMountedCleanup = null
                }
            },
            attach() {
                this.cleanup()

                if (options.whileElementsMounted === undefined) {
                    this.update()
                    return
                }

                if (
                    this.referenceElement != null &&
                    this.floatingElement != null
                ) {
                    this.whileElementsMountedCleanup =
                        options.whileElementsMounted(
                            this.referenceElement,
                            this.floatingElement,
                            this.update
                        )
                }
            },
            reset() {
                if (!this.openOption) {
                    this.isPositioned = false
                }
            },
            destroy() {
                this.cleanup()
                this.$destroy()
            },
        },
    })

    return {
        get x() {
            return vm.x
        },
        get y() {
            return vm.y
        },
        get strategy() {
            return vm.strategy
        },
        get placement() {
            return vm.placement
        },
        get middlewareData() {
            return vm.middlewareData
        },
        get isPositioned() {
            return vm.isPositioned
        },
        get floatingStyles() {
            return vm.floatingStyles
        },
        update: vm.update.bind(vm),
        destroy: vm.destroy.bind(vm),
    }
}

export { arrow, useFloating }
